import apiClient from '@/lib/axios'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'

export interface DashboardData {
  context?: {
    restaurantId?: string
    restaurantName?: string
    branchId?: string
    branchName?: string
    scope: 'platform' | 'restaurant' | 'branch'
  }
  restaurants: {
    count: number
    info?: {
      id: string
      name: string
    }
  }
  branches: {
    count: number
  }
  menuItems: {
    count: number
  }
  activeSessions: {
    count: number
  }
  recentOrders: Array<{
    id: string
    status: string
    total: number
    createdAt: string
  }>
  metrics: {
    ordersToday: {
      count: number
    }
    revenueToday: {
      amount: number
      currency: string
    }
  }
}

interface DashboardResponse {
  success: boolean
  data: DashboardData
}

export function useDashboard(restaurantId?: string | null, branchId?: string | null) {
  const { getToken } = useAuth()

  return useQuery<DashboardData>({
    queryKey: ['dashboard', restaurantId, branchId],
    queryFn: async () => {
      try {
        const token = await getToken()
        if (!token) {
          throw new Error('No authentication token available. Please sign in again.')
        }

        // Build query params for context switching
        const params = new URLSearchParams()
        if (restaurantId) {
          params.append('restaurantId', restaurantId)
        }
        if (branchId) {
          params.append('branchId', branchId)
        }

        const queryString = params.toString()
        const url = `/admin/dashboard${queryString ? `?${queryString}` : ''}`

        // Build headers - include X-Branch-ID if branchId is provided
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
        }
        if (branchId) {
          headers['X-Branch-ID'] = branchId
        }

        const response = await apiClient.get<DashboardResponse>(url, {
          headers,
        })

        if (!response.data.success) {
          // Check if there's an error message in the response
          const errorMessage = (response.data as { error?: { message?: string } }).error?.message || 'Failed to fetch dashboard data'
          throw new Error(errorMessage)
        }

        console.warn('Dashboard data fetched successfully:', response.data.data)
        return response.data.data
      } catch (error) {
        // Enhance error messages for better debugging
        const axiosError = error as AxiosError<{ error?: { message?: string } }>
        if (axiosError.response) {
          const status = axiosError.response.status
          const errorData = axiosError.response.data

          if (status === 401) {
            throw new Error('401: Unauthorized - Your session may have expired. Please sign in again.')
          } else if (status === 403) {
            const message = errorData?.error?.message || '403: Forbidden - Access denied'
            throw new Error(message)
          } else if (status === 404) {
            throw new Error('404: Not Found - Dashboard endpoint not found')
          } else if (status >= 500) {
            throw new Error(`500: Server Error - ${errorData?.error?.message || 'Internal server error'}`)
          } else {
            throw new Error(errorData?.error?.message || `Request failed with status ${status}`)
          }
        } else if (axiosError.request) {
          throw new Error('Network error - Could not reach the server. Check your connection.')
        } else {
          throw error
        }
      }
    },
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes (formerly cacheTime)
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors (auth issues)
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false
      }
      return failureCount < 2
    },
  })
}

